import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Chip, Stack, styled, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

import { BillingItem } from 'domain/entities/BillingItem';
import { getPriceSummary, JobPriceModifier } from 'domain/entities/JobPriceModifer';
import { Entity } from 'domain/types/Entity';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import formatPrice from 'infrastructure/utils/formatPrice';
import Modal from 'targets/web/components/Modal';
import { configurationRoutes } from 'targets/web/modules/configuration/navigation/configurationRoutes';

interface ConfirmBillingItemModalProps {
  open: boolean;
  invoiceHdrId: string;
  services: BillingItem['services'];
  priceModifiers: Entity<JobPriceModifier>[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const Container = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.neutral,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

export const ConfirmBillingItemModal: FC<ConfirmBillingItemModalProps> = ({
  open,
  invoiceHdrId,
  services,
  priceModifiers,
  onClose,
  onConfirm,
}) => {
  const t = useTranslationPrefix('billing.confirm_billing_item_modal');
  const mt = useTranslationPrefix('billing.billing_review.modifier');
  const tGlobal = useTranslationPrefix('global');

  const navigate = useNavigate();

  const { total, modifiers, subtotal, servicesSummary } = getPriceSummary(
    services.map((s) => ({ ...s.service, quantity: s.quantity })),
    priceModifiers,
  );

  const displayedModifiers = useMemo(() => modifiers.filter((m) => !m.disabled), [modifiers]);

  const hasMissingPrice = useMemo(() => !!services.find((s) => s.price === null), [services]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onConfirmClick = useCallback(() => {
    setIsSubmitting(true);
    onConfirm().finally(() => {
      setIsSubmitting(false);
      onClose();
    });
  }, [onClose, onConfirm]);

  return (
    <Modal
      open={open}
      title={t('title')}
      description={
        <Typography color="text.secondary">
          <Trans
            i18nKey="billing.confirm_billing_item_modal.description"
            components={{ bold: <Typography variant="labelLarge" color="text.primary" /> }}
          />
          <br />
          <Typography mt={4} variant="caption">
            {invoiceHdrId ? t('job_invoice_hdr') + ' - ' + invoiceHdrId : null}
          </Typography>
        </Typography>
      }
      maxWidth={'xs'}
      onClose={onClose}
      content={
        <Stack gap={4}>
          <Stack gap={2}>
            {servicesSummary.map(({ code, id, discountPercentage, afterDiscount, quantity }) => (
              <Stack key={id} gap={1}>
                <Container direction="row" data-testname={`service-${id}`}>
                  <Typography variant="body2" color="text.secondary" data-testname="serviceCode">
                    {code} (quantity: {quantity}
                    {discountPercentage ? `, ${discountPercentage}%` : ''})
                  </Typography>

                  {afterDiscount ? (
                    <Typography variant="body2" fontWeight="bold" data-testname="servicePrice">
                      {formatPrice(Number(afterDiscount))}
                    </Typography>
                  ) : (
                    <Chip
                      size="small"
                      color="error"
                      onClick={() =>
                        navigate(
                          `${configurationRoutes.configuration}/${configurationRoutes.internalRoutes.priceLists}`,
                        )
                      }
                      icon={<OpenInNewOutlinedIcon />}
                      label={t('no_price_set_label')}
                    />
                  )}
                </Container>
                {!afterDiscount && (
                  <Typography variant="inputHelper" color="error">
                    {t('no_price_set_helper', { service: code })}
                  </Typography>
                )}
              </Stack>
            ))}
            <Stack gap={1}>
              <Container direction="row">
                <Typography variant="body2" color="text.secondary">
                  {t('subtotal_label')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatPrice(Number(subtotal))}
                </Typography>
              </Container>
            </Stack>
          </Stack>

          <Stack gap={2}>
            {displayedModifiers.map((m) => (
              <Stack key={m.type} gap={1}>
                <Container direction="row">
                  <Typography variant="body2" color="text.secondary">
                    {mt(m.type, { modifier: m.label, absoluteModifier: m.absoluteLabel })}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPrice(Number(m.value))}
                  </Typography>
                </Container>
              </Stack>
            ))}
          </Stack>

          <Container direction="row">
            <Typography variant="body2" color="text.secondary">
              {t('total_label')}
            </Typography>

            <Typography variant="body2" fontWeight="bold" data-testname="totalCost">
              {formatPrice(Number(total))}
            </Typography>
          </Container>
        </Stack>
      }
      actions={{
        primary: {
          type: 'submit',
          text: tGlobal('confirm'),
          onClick: onConfirmClick,
          disabled: hasMissingPrice,
          loading: isSubmitting,
        },
        secondary: {
          text: tGlobal('cancel'),
        },
      }}
    />
  );
};
